import { Services } from '@Shared.Angular/@types/services';
import { IHttpService, IPromise } from 'angular';
import { Guid } from '@Shared.Angular/@types/guid';
import DocumentType from '@Shared.Angular/@types/core/contracts/constants/documentType';
import IModelerWorkflowDetail from '@Shared.Angular/@types/core/contracts/queryModel/modeler/modelerWorkflowDetail';

/**
 * @ngdoc service
 * @name documentApiService
 * @module flowingly.services
 *
 * @description A service for fetching generated documents
 */

export default class DocumentApiService {
  private apiBase: string;

  private pendingRequests: { [key: Guid]: { [key: number]: IPromise<void> } } =
    {};

  constructor(
    private $http: angular.IHttpService,
    private APP_CONFIG: Services.APP_CONFIG,
    private fileService: Services.FileService
  ) {
    this.apiBase = this.APP_CONFIG.apiBaseUrl + 'documents/';
  }

  private downloadFlowModelSchemaPdf(
    flowModelSchemaId: Guid,
    type: DocumentType
  ) {
    if (!this.pendingRequests[flowModelSchemaId]) {
      this.pendingRequests[flowModelSchemaId] = {};
    } else if (this.pendingRequests[flowModelSchemaId][type]) {
      return this.pendingRequests[flowModelSchemaId][type];
    }
    this.pendingRequests[flowModelSchemaId][type] = this.$http
      .get<IDocumentDownload>(
        `${this.apiBase}flowmodelschemas/${flowModelSchemaId}?type=${type}`
      )
      .then((response) => {
        const document = response.data;
        this.fileService.download(
          document.data,
          document.filename,
          document.mimeType
        );
      })
      .finally(() => {
        delete this.pendingRequests[flowModelSchemaId][type];
      });
    return this.pendingRequests[flowModelSchemaId][type];
  }

  public downloadFlowModelSchemaStepsPdf(flowModelSchemaId: Guid) {
    return this.downloadFlowModelSchemaPdf(
      flowModelSchemaId,
      DocumentType.FlowModelSchemaSteps
    );
  }

  public downloadFlowModelSchemaMapPdf(flowModelSchemaId: Guid) {
    return this.downloadFlowModelSchemaPdf(
      flowModelSchemaId,
      DocumentType.FlowModelSchemaMap
    );
  }

  public downloadFlowModelSchemaMapAndStepsPdf(flowModelSchemaId: Guid) {
    return this.downloadFlowModelSchemaPdf(
      flowModelSchemaId,
      DocumentType.FlowModelSchemaMapAndSteps
    );
  }

  public getFlowModelSchema(flowModelSchemaId: Guid) {
    return this.$http
      .get<IModelerWorkflowDetail>(
        `${this.apiBase}flowModelSchemas/${flowModelSchemaId}`
      )
      .then((response) => {
        return response.data;
      });
  }

  public downloadFlowPdf(flowId: Guid, type: DocumentType) {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!this.pendingRequests[flowId]) {
      this.pendingRequests[flowId] = {};
    } else if (this.pendingRequests[flowId][type]) {
      return this.pendingRequests[flowId][type];
    }
    this.pendingRequests[flowId][type] = this.$http
      .get<IDocumentDownload>(
        `${this.apiBase}flows/${flowId}?type=${type}&timeZone=${userTimeZone}`
      )
      .then((response) => {
        const document = response.data;
        this.fileService.download(
          document.data,
          document.filename,
          document.mimeType
        );
      })
      .finally(() => {
        delete this.pendingRequests[flowId][type];
      });
    return this.pendingRequests[flowId][type];
  }

  private downloadFlowModelPdf(
    flowModelId: Guid,
    type: DocumentType,
    noSpinner = false
  ) {
    if (!this.pendingRequests[flowModelId]) {
      this.pendingRequests[flowModelId] = {};
    } else if (this.pendingRequests[flowModelId][type]) {
      return this.pendingRequests[flowModelId][type];
    }
    this.pendingRequests[flowModelId][type] = this.$http
      .get<IDocumentDownload>(
        `${this.apiBase}flowmodels/${flowModelId}?type=${type}`,
        {
          noSpinner
        }
      )
      .then((response) => {
        const document = response.data;
        this.fileService.download(
          document.data,
          document.filename,
          document.mimeType
        );
      })
      .finally(() => {
        delete this.pendingRequests[flowModelId][type];
      });
    return this.pendingRequests[flowModelId][type];
  }

  public downloadFlowModelStepsPdf(flowModelId: Guid) {
    return this.downloadFlowModelPdf(
      flowModelId,
      DocumentType.FlowModelSchemaSteps,
      true
    );
  }

  public downloadFlowModelMapPdf(flowModelId: Guid) {
    return this.downloadFlowModelPdf(
      flowModelId,
      DocumentType.FlowModelSchemaMap,
      true
    );
  }

  public downloadFlowModelMapAndStepsPdf(flowModelId: Guid) {
    return this.downloadFlowModelPdf(
      flowModelId,
      DocumentType.FlowModelSchemaMapAndSteps,
      true
    );
  }

  public downloadFlowModelParentMapAndStepsPdf(flowModelId: Guid) {
    return this.downloadFlowModelPdf(
      flowModelId,
      DocumentType.FlowModelSchemaParentMapAndSteps,
      true
    );
  }

  public downloadProcedurePdf(procedureId: Guid) {
    return this.downloadProcedureDocument(
      procedureId,
      DocumentType.ProcedureSchema
    );
  }

  private downloadProcedureDocument(procedureId: Guid, type: DocumentType) {
    if (!this.pendingRequests[procedureId]) {
      this.pendingRequests[procedureId] = {};
    } else if (this.pendingRequests[procedureId][type]) {
      return this.pendingRequests[procedureId][type];
    }
    this.pendingRequests[procedureId][type] = this.$http
      .get<IDocumentDownload>(
        `${this.apiBase}procedures/${procedureId}?type=${type}`
      )
      .then((response) => {
        const document = response.data;
        this.fileService.download(
          document.data,
          document.filename,
          document.mimeType
        );
      })
      .finally(() => {
        delete this.pendingRequests[procedureId][type];
      });
    return this.pendingRequests[procedureId][type];
  }
}
angular
  .module('flowingly.services')
  .factory('documentApiService', [
    '$http',
    'APP_CONFIG',
    'fileService',
    (
      $http: IHttpService,
      APP_CONFIG: Services.APP_CONFIG,
      fileService: Services.FileService
    ) => new DocumentApiService($http, APP_CONFIG, fileService)
  ]);
export type DocumentApiServiceType = InstanceType<typeof DocumentApiService>;

interface IDocumentDownload {
  data: [];
  filename: string;
  mimeType: string;
}
